import { getReviewTriggerType } from 'pages/employee-reviews/shared/triggers';

// The server's wording, so a message reads the same whether the page or the API catches it.
export const duplicateTriggerMessage = "The same trigger has been added more than once.";

export function redundantTriggerMessage(coveredSentence, coveringSentence) {
    return `'${coveredSentence}' can never be met before '${coveringSentence}'.`;
}

export function redundantReviewTypeTriggerMessage(coveredSentence, coveringSentence) {
    return `'${coveredSentence}' can never be met before the review type's trigger '${coveringSentence}'.`;
}

export function getTopicIdByKey(topicKey, reviewOptions) {
    return (reviewOptions?.topics ?? []).find((topic) => topic.key === topicKey)?.id ?? null;
}

export function getTopicKeyById(topicId, reviewOptions) {
    return (reviewOptions?.topics ?? []).find((topic) => topic.id === topicId)?.key ?? null;
}

export function getTopicIdsByKeys(topicKeys, reviewOptions) {
    return (topicKeys ?? [])
        .map((topicKey) => getTopicIdByKey(topicKey, reviewOptions))
        .filter((topicId) => topicId != null);
}

// A trigger type is offered only while its topic is one of the given topics and there is a
// condition editor for it.
export function getTriggerTypesForTopics(topicIds, reviewOptions) {
    const offeredTopicIds = topicIds ?? [];

    return (reviewOptions?.triggerTypes ?? [])
        .filter((triggerType) => getReviewTriggerType(triggerType.key) != null && offeredTopicIds.includes(triggerType.topicId));
}

let lastTriggerClientId = 0;

// Rows are keyed by an id of the page's own, so removing one leaves every other row's controls
// with the row they belong to. It is never sent to the API.
function nextTriggerClientId() {
    lastTriggerClientId++;
    return `trigger-${lastTriggerClientId}`;
}

// A row that has not been saved has no condition to compare against and no added date.
export function buildNewTrigger(triggerType) {
    return {
        clientId: nextTriggerClientId(),
        triggerKey: triggerType.key,
        topicId: triggerType.topicId,
        condition: getReviewTriggerType(triggerType.key).buildDefaultCondition(),
        loadedCondition: null,
        createdDate: null,
        addedWithReviewType: false
    };
}

// Each trigger type has its own condition, so a row switched to another type starts again from
// that type's default condition, as a new trigger.
export function changeTriggerType(trigger, triggerType) {
    if (trigger.triggerKey === triggerType.key) {
        return trigger;
    }

    return buildNewTrigger(triggerType);
}

// Only triggers with a condition editor become rows. The loaded condition is kept beside the
// editable one so the row can tell whether it still matches what was saved, and so whether its
// added date still applies.
export function mapTriggersFromApi(apiTriggers, reviewOptions) {
    return (apiTriggers ?? [])
        .filter((apiTrigger) => getReviewTriggerType(apiTrigger.triggerKey) != null)
        .map((apiTrigger) => {
            const triggerType = getReviewTriggerType(apiTrigger.triggerKey);

            return {
                clientId: nextTriggerClientId(),
                triggerKey: apiTrigger.triggerKey,
                topicId: getTopicIdByKey(apiTrigger.topicKey, reviewOptions),
                condition: triggerType.conditionFromApi(apiTrigger),
                loadedCondition: triggerType.conditionFromApi(apiTrigger),
                createdDate: apiTrigger.createdDate ?? null,
                addedWithReviewType: apiTrigger.addedWithReviewType === true
            };
        });
}

// A trigger with no condition editor cannot be shown or edited, but a save replaces every
// trigger, so it is held in its request form and sent back unchanged. When it was added is
// returned by the API but refused on a save, so it is left out.
export function toUnregisteredTriggerRequest(apiTrigger) {
    const requestTrigger = { ...apiTrigger };
    delete requestTrigger.createdDate;
    delete requestTrigger.addedWithReviewType;

    return requestTrigger;
}

export function getUnregisteredTriggerRequests(apiTriggers) {
    return (apiTriggers ?? [])
        .filter((apiTrigger) => getReviewTriggerType(apiTrigger.triggerKey) == null)
        .map(toUnregisteredTriggerRequest);
}

// Saving a changed condition stores it as a new trigger, so a row only still holds what was
// saved while its condition matches the one it was loaded with.
export function isTriggerSaved(trigger) {
    const triggerType = getReviewTriggerType(trigger?.triggerKey);

    return triggerType != null && trigger.loadedCondition != null && triggerType.isSameCondition(trigger.condition, trigger.loadedCondition);
}

export function describeTrigger(trigger, reviewOptions) {
    return getReviewTriggerType(trigger.triggerKey).describe(trigger.condition, reviewOptions);
}

// Only the properties the API defines for a trigger are sent; when it was added and the loaded
// condition are page state, and the API refuses a trigger carrying them.
export function toTriggerRequest(trigger, reviewOptions) {
    return {
        triggerKey: trigger.triggerKey,
        topicKey: getTopicKeyById(trigger.topicId, reviewOptions),
        ...getReviewTriggerType(trigger.triggerKey).conditionToApi(trigger.condition)
    };
}

export function getTriggerErrorFieldName(index, conditionField = null) {
    if (conditionField == null) {
        return `triggers.${index}`;
    }

    return `triggers.${index}.${conditionField}`;
}

// The messages a trigger type reported against the row, keyed by its own condition fields.
export function getTriggerConditionErrors(errors, index) {
    const prefix = getTriggerErrorFieldName(index, "");
    const conditionErrors = {};

    Object.keys(errors ?? {}).forEach((fieldName) => {
        if (fieldName.startsWith(prefix)) {
            conditionErrors[fieldName.substring(prefix.length)] = errors[fieldName];
        }
    });

    return conditionErrors;
}

// Every message reported against the triggers, in row order, each naming its row so the
// messages can be listed together beneath the triggers table.
export function getTriggerErrorMessages(errors, triggerCount) {
    const messages = [];

    for (let index = 0; index < triggerCount; index++) {
        const rowMessages = [
            ...Object.values(getTriggerConditionErrors(errors, index)),
            errors?.[getTriggerErrorFieldName(index)]
        ].filter((message) => message != null);

        rowMessages.forEach((message) => {
            messages.push(`Trigger ${index + 1}: ${message}`);
        });
    }

    return messages;
}

// The errors object the triggers table reads, from validation rules keyed by row and field.
export function toTriggerErrors(validationRules) {
    const errors = {};

    validationRules.forEach((rule) => {
        if (errors[rule.fieldName] == null) {
            errors[rule.fieldName] = rule.errorMessage;
        }
    });

    return errors;
}

function findCoveringTrigger(trigger, candidates) {
    const triggerType = getReviewTriggerType(trigger.triggerKey);

    return candidates.find((candidate) => {
        return candidate !== trigger &&
            candidate.triggerKey === trigger.triggerKey &&
            triggerType.covers(candidate.condition, trigger.condition);
    }) ?? null;
}

// Mirrors the server's trigger rules, reported against the row they belong to and in row order,
// so the first rule is always the first invalid row's. Each trigger type validates its own
// condition. A complete row is then checked against the rows above it, so a repeated trigger is
// reported once, against the repeat. A row that is still complete is then redundant when one of
// the fixed covering triggers, or another complete row, would always be met no later than it;
// the message goes against that looser row only.
//
// checkedTriggers are the rows the rules apply to (all of them unless given); the rest are
// neither reported nor compared against. coveringTriggers are triggers that cannot be changed
// here, such as a review type's own beside a driver's.
export function getTriggerValidationRules(triggers, reviewOptions, { checkedTriggers = null, coveringTriggers = [] } = {}) {
    const rows = triggers ?? [];
    const checkedRows = checkedTriggers ?? rows;
    const rowRules = rows.map(() => []);
    const completeRows = [];

    rows.forEach((trigger, index) => {
        if (!checkedRows.includes(trigger)) {
            return;
        }

        const triggerType = getReviewTriggerType(trigger.triggerKey);
        const conditionErrors = triggerType.validateCondition(trigger.condition, reviewOptions);
        const conditionFields = Object.keys(conditionErrors);

        conditionFields.forEach((conditionField) => {
            rowRules[index].push({
                fieldName: getTriggerErrorFieldName(index, conditionField),
                valid: false,
                errorMessage: conditionErrors[conditionField]
            });
        });

        if (conditionFields.length > 0) {
            return;
        }

        const repeated = rows.slice(0, index).some((earlier) => {
            return checkedRows.includes(earlier) &&
                earlier.triggerKey === trigger.triggerKey &&
                triggerType.isSameCondition(earlier.condition, trigger.condition);
        });

        if (repeated) {
            rowRules[index].push({
                fieldName: getTriggerErrorFieldName(index),
                valid: false,
                errorMessage: duplicateTriggerMessage
            });
            return;
        }

        completeRows.push(trigger);
    });

    rows.forEach((trigger, index) => {
        if (!completeRows.includes(trigger)) {
            return;
        }

        const coveringFixedTrigger = findCoveringTrigger(trigger, coveringTriggers);
        let errorMessage = null;

        if (coveringFixedTrigger != null) {
            errorMessage = redundantReviewTypeTriggerMessage(describeTrigger(trigger, reviewOptions), describeTrigger(coveringFixedTrigger, reviewOptions));
        } else {
            const coveringRow = findCoveringTrigger(trigger, completeRows);

            if (coveringRow != null) {
                errorMessage = redundantTriggerMessage(describeTrigger(trigger, reviewOptions), describeTrigger(coveringRow, reviewOptions));
            }
        }

        if (errorMessage != null) {
            rowRules[index].push({
                fieldName: getTriggerErrorFieldName(index),
                valid: false,
                errorMessage: errorMessage
            });
        }
    });

    return rowRules.flat();
}
